import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getLocalToday } from '../../../../../config/timezone';
import { useNotification } from '../../../../../hooks/use-notification';
import {
  AiExpirySuggestionLimitError,
  type SuggestExpiryDateResponse,
} from '../../../../../services/goods-receiving/goods-receiving.types';
import { formatDate } from '../../../../../utils/date-formatter';
import { formatDateForInput, parseDate } from '../../../../../utils/date-utils';
import { breakPaymentKeywords } from '../../../../../utils/payment-autofill';
import type { AddItemFormData } from '../../../hooks/use-add-item-form';
import { useSuggestExpiryDate } from '../../../hooks/use-suggest-expiry-date';
import { useExtractedData } from '../../extracted-data-context';
import { useShouldHighlightAiGaps } from '../ai-gap-banner';
import { SectionHeader } from '../section-header';

const QUICK_EXPIRY_DAYS = [1, 3, 5] as const;

interface TraceabilityStepProps {
  isMobile: boolean;
  /** Optional context carried from the wizard draft to enrich the AI prompt. */
  wizardContext?: {
    supplierName?: string;
    documentDate?: string;
    documentNumber?: string;
  };
}

// "DDT 4521 del 18/7/2026" — reference for loose produce (ortofrutta) that has
// no lot number of its own. Empty when the DDT has neither a number nor a date.
function buildDdtBatchNumber(wizardContext: TraceabilityStepProps['wizardContext']): string {
  const documentNumber = wizardContext?.documentNumber?.trim();
  const formattedDate = wizardContext?.documentDate ? formatDate(wizardContext.documentDate) : '';
  if (!documentNumber && !formattedDate) {
    return '';
  }
  return ['DDT', documentNumber, formattedDate ? `del ${formattedDate}` : null]
    .filter(Boolean)
    .join(' ');
}

// Switch between the product lot and the DDT reference on toggle.
// ON: prefill the DDT reference only when the row has no lot yet — a lot read
// from the DDT (or typed) is never overwritten here; the user opts in through
// "Usa comunque il riferimento DDT".
// OFF: restore the remembered lot.
// Returns the batchNumber to set, or null to leave the field as-is.
function nextBatchNumberOnLooseProduceToggle(input: {
  checked: boolean;
  batchNumber: string;
  savedLot: string;
  ddtPrefill: string;
}): string | null {
  if (input.checked) {
    const hasExistingLot = input.batchNumber.trim() !== '';
    return !hasExistingLot && input.ddtPrefill ? input.ddtPrefill : null;
  }
  return input.savedLot !== input.batchNumber ? input.savedLot : null;
}

function buildWarningProps(showWarning: boolean) {
  if (!showWarning) {
    return { color: undefined, focused: undefined, helperSx: undefined };
  }
  return {
    color: 'warning' as const,
    focused: true,
    helperSx: { sx: { color: 'warning.main' } },
  };
}

function getQuickExpiryActiveValue(expiryDateValue: Date | null): number | null {
  if (!expiryDateValue) {
    return null;
  }
  const today = getLocalToday();
  const diffDays = Math.round((expiryDateValue.getTime() - today.getTime()) / 86_400_000);
  return QUICK_EXPIRY_DAYS.includes(diffDays as (typeof QUICK_EXPIRY_DAYS)[number])
    ? diffDays
    : null;
}

function getAiButtonState(state: {
  hasNoExpiry: boolean;
  hasProductName: boolean;
  isPending: boolean;
}): { disabled: boolean; tooltipKey: string } {
  const disabled = !state.hasProductName || state.hasNoExpiry || state.isPending;
  if (state.hasNoExpiry) {
    return { disabled, tooltipKey: 'aiExpirySuggestionDisabledNoExpiry' };
  }
  if (!state.hasProductName) {
    return { disabled, tooltipKey: 'aiExpirySuggestionMissingProduct' };
  }
  if (state.isPending) {
    return { disabled, tooltipKey: 'aiExpirySuggestionLoading' };
  }
  return { disabled, tooltipKey: '' };
}

function deriveAiGapWarnings(input: {
  shouldHighlight: boolean;
  batchNumber: string;
  batchNumberError?: string;
  expiryDate: string;
  expiryDateError?: string;
  hasNoExpiry: boolean;
}) {
  return {
    batchWarn: input.shouldHighlight && !input.batchNumber?.trim() && !input.batchNumberError,
    expiryWarn:
      input.shouldHighlight &&
      !input.expiryDate?.trim() &&
      !input.expiryDateError &&
      !input.hasNoExpiry,
  };
}

function deriveAiSuggestionFlags(
  aiSuggestion: SuggestExpiryDateResponse | null,
  hasNoExpiry: boolean,
) {
  return {
    showDateAiDisclaimer: !!aiSuggestion?.hasExpiry && !!aiSuggestion.suggestedDate,
    showShelfStableSuggestion:
      aiSuggestion !== null && aiSuggestion.hasExpiry === false && !hasNoExpiry,
    showAiUnableToEstimate:
      aiSuggestion !== null && aiSuggestion.hasExpiry === true && !aiSuggestion.suggestedDate,
  };
}

function useExpiryHandlers(clearAiSuggestion: () => void) {
  const { setValue } = useFormContext<AddItemFormData>();
  const handleExpiryChange = (date: Date | null) => {
    setValue('expiryDate', date ? formatDateForInput(date) : '', { shouldValidate: true });
    clearAiSuggestion();
  };
  const handleQuickExpiry = (days: number) => {
    const today = getLocalToday();
    const target = new Date(today.getFullYear(), today.getMonth(), today.getDate() + days);
    setValue('expiryDate', formatDateForInput(target), { shouldValidate: true });
    clearAiSuggestion();
  };
  const handleHasNoExpiryToggle = (checked: boolean) => {
    setValue('hasNoExpiry', checked, { shouldValidate: true });
    if (checked) {
      setValue('expiryDate', '', { shouldValidate: true });
    }
    clearAiSuggestion();
  };
  return { handleExpiryChange, handleQuickExpiry, handleHasNoExpiryToggle };
}

function useLooseProduceToggle(
  wizardContext: TraceabilityStepProps['wizardContext'],
  batchNumber: string | undefined,
  isLooseProduce: boolean,
) {
  const { setValue } = useFormContext<AddItemFormData>();
  // Holds the lot present before switching to loose-produce, so turning the
  // toggle back off restores it instead of clearing the field.
  const savedLotRef = useRef('');
  const ddtReference = buildDdtBatchNumber(wizardContext);
  const handleToggle = (checked: boolean) => {
    setValue('isLooseProduce', checked, { shouldValidate: true });
    if (checked) {
      savedLotRef.current = batchNumber ?? '';
    }
    const nextBatchNumber = nextBatchNumberOnLooseProduceToggle({
      checked,
      batchNumber: batchNumber ?? '',
      savedLot: savedLotRef.current,
      ddtPrefill: ddtReference,
    });
    if (nextBatchNumber !== null) {
      setValue('batchNumber', nextBatchNumber, { shouldValidate: true });
    }
  };
  const handleUseDdtReference = () => {
    if (!ddtReference) {
      return;
    }
    savedLotRef.current = batchNumber ?? '';
    setValue('batchNumber', ddtReference, { shouldValidate: true });
  };
  const currentLot = batchNumber?.trim() ?? '';
  // Without a DDT number/date there is no reference to switch to: a warning without an action would only confuse.
  const showExistingLotWarning =
    isLooseProduce && currentLot !== '' && ddtReference !== '' && currentLot !== ddtReference;
  return {
    handleToggle,
    handleUseDdtReference,
    canUseDdtReference: ddtReference !== '',
    showExistingLotWarning,
  };
}

function useTraceabilityExtraction() {
  const { setValue } = useFormContext<AddItemFormData>();
  const { extractedData, consumeFields } = useExtractedData();
  useEffect(() => {
    if (!extractedData) {
      return;
    }
    const consumed = consumeFields(['batchNumber', 'expiryDate']);
    if (!consumed) {
      return;
    }
    if (consumed.batchNumber) {
      setValue('batchNumber', consumed.batchNumber, { shouldValidate: true });
    }
    if (consumed.expiryDate) {
      setValue('expiryDate', consumed.expiryDate, { shouldValidate: true });
    }
  }, [extractedData, setValue, consumeFields]);
}

// Owns the AI expiry-suggestion state and request handling. Reads the latest
// field values at click time via getValues, so it never subscribes to renders.
function useAiExpirySuggestion(wizardContext: TraceabilityStepProps['wizardContext']) {
  const { getValues, setValue } = useFormContext<AddItemFormData>();
  const { showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');

  // Holds the AI response so the disclaimer / shelf-stable banner can render
  // until the operator overrides the field manually. Stored as the full
  // response (not just a boolean) so we can show the reasoning verbatim.
  const [aiSuggestion, setAiSuggestion] = useState<SuggestExpiryDateResponse | null>(null);
  const suggestMutation = useSuggestExpiryDate();

  const clearAiSuggestion = () => setAiSuggestion(null);

  const handleAiSuggest = async () => {
    const { productName, quantity, unit, batchNumber } = getValues();
    if (!productName?.trim()) {
      showError(t('aiExpirySuggestionMissingProduct'));
      return;
    }
    try {
      const result = await suggestMutation.mutateAsync({
        productName: productName.trim(),
        quantity: quantity?.trim() || undefined,
        unit: unit?.trim() || undefined,
        batchNumber: batchNumber?.trim() || undefined,
        supplierName: wizardContext?.supplierName,
        documentDate: wizardContext?.documentDate,
      });
      setAiSuggestion(result);
      if (result.hasExpiry && result.suggestedDate) {
        setValue('expiryDate', result.suggestedDate, { shouldValidate: true });
      }
    } catch (err) {
      if (err instanceof AiExpirySuggestionLimitError) {
        showError(t('aiExpirySuggestionLimitReached'));
        return;
      }
      showError(t('aiExpirySuggestionError'));
    }
  };

  return { aiSuggestion, suggestMutation, clearAiSuggestion, handleAiSuggest };
}

interface QuickExpiryRowProps {
  isMobile: boolean;
  activeValue: number | null;
  disabled: boolean;
  onSelect: (days: number) => void;
  isSuggesting: boolean;
  aiDisabled: boolean;
  aiTooltipKey: string;
  onAiSuggest: () => void;
}

// Quick-expiry row: chip group + AI button.
// Desktop: chips and AI button on a single line.
// Mobile: chips and button share the top row.
function QuickExpiryRow({
  isMobile,
  activeValue,
  disabled,
  onSelect,
  isSuggesting,
  aiDisabled,
  aiTooltipKey,
  onAiSuggest,
}: QuickExpiryRowProps) {
  const { t } = useTranslation('goodsReceiving');

  const aiButton = (
    <Tooltip title={aiTooltipKey ? t(aiTooltipKey) : ''}>
      <span>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            isSuggesting ? <CircularProgress size={14} /> : <AutoAwesomeOutlined fontSize="small" />
          }
          onClick={onAiSuggest}
          disabled={aiDisabled}
          aria-label={t('aiExpirySuggestionCta')}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {isSuggesting ? t('aiExpirySuggestionLoading') : t('aiExpirySuggestionCta')}
        </Button>
      </span>
    </Tooltip>
  );

  const toggles = (
    <ToggleButtonGroup
      value={activeValue}
      exclusive
      disabled={disabled}
      onChange={(_event, value: number | null) => {
        if (value !== null) {
          onSelect(value);
        }
      }}
      color="primary"
      size="small"
      aria-label={t('quickExpiryLabel')}
      sx={{ alignSelf: isMobile ? 'flex-start' : 'auto', flexShrink: 0 }}
    >
      {QUICK_EXPIRY_DAYS.map((days) => (
        <ToggleButton key={days} value={days} sx={{ textTransform: 'none', px: 2.5 }}>
          {t('quickExpiryChip', { count: days })}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          {toggles}
          {aiButton}
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5 }}>
      {toggles}
      <Box sx={{ ml: 'auto' }}>{aiButton}</Box>
    </Box>
  );
}

function ExistingLotWarning({
  visible,
  canUseDdtReference,
  onUseDdtReference,
}: {
  visible: boolean;
  canUseDdtReference: boolean;
  onUseDdtReference: () => void;
}) {
  const { t } = useTranslation('goodsReceiving');
  if (!visible) {
    return null;
  }
  return (
    <Alert
      severity="warning"
      action={
        canUseDdtReference ? (
          <Button color="inherit" size="small" onClick={onUseDdtReference} sx={{ minHeight: 48 }}>
            {t('looseProduceUseDdtReference')}
          </Button>
        ) : undefined
      }
    >
      {t('looseProduceExistingLotWarning')}
    </Alert>
  );
}

export function TraceabilityStep({ isMobile, wizardContext }: TraceabilityStepProps) {
  const { t } = useTranslation('goodsReceiving');
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AddItemFormData>();

  const productName = watch('productName');
  const batchNumber = watch('batchNumber');
  const expiryDate = watch('expiryDate');
  const hasNoExpiry = watch('hasNoExpiry') ?? false;
  const isLooseProduce = Boolean(watch('isLooseProduce'));
  const expiryDateValue = parseDate(expiryDate || '');

  useTraceabilityExtraction();

  const { aiSuggestion, suggestMutation, clearAiSuggestion, handleAiSuggest } =
    useAiExpirySuggestion(wizardContext);

  const batchNumberError = errors.batchNumber?.message as string | undefined;
  const expiryDateError = errors.expiryDate?.message as string | undefined;

  const shouldHighlightAiGaps = useShouldHighlightAiGaps();
  const { batchWarn, expiryWarn } = deriveAiGapWarnings({
    shouldHighlight: shouldHighlightAiGaps,
    batchNumber,
    batchNumberError,
    expiryDate,
    expiryDateError,
    hasNoExpiry,
  });
  const aiHint = t('aiGap.fieldHint');

  const batchWarnProps = buildWarningProps(batchWarn);
  const expiryWarnProps = buildWarningProps(expiryWarn);

  const { handleExpiryChange, handleQuickExpiry, handleHasNoExpiryToggle } =
    useExpiryHandlers(clearAiSuggestion);

  const {
    handleToggle: handleIsLooseProduceToggle,
    handleUseDdtReference,
    canUseDdtReference,
    showExistingLotWarning,
  } = useLooseProduceToggle(wizardContext, batchNumber, isLooseProduce);

  const quickExpiryActiveValue = getQuickExpiryActiveValue(expiryDateValue);

  const { disabled: aiButtonDisabled, tooltipKey: aiButtonTooltipKey } = getAiButtonState({
    hasNoExpiry,
    hasProductName: !!productName?.trim(),
    isPending: suggestMutation.isPending,
  });
  const { showDateAiDisclaimer, showShelfStableSuggestion, showAiUnableToEstimate } =
    deriveAiSuggestionFlags(aiSuggestion, hasNoExpiry);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionHeader title={t('traceability')} helper={t('traceabilityHelper')} />
      <TextField
        label={breakPaymentKeywords(t('batchNumberLabel'))}
        value={batchNumber || ''}
        onChange={(e) => setValue('batchNumber', e.target.value, { shouldValidate: true })}
        fullWidth
        required
        error={!!batchNumberError}
        color={batchWarnProps.color}
        focused={batchWarnProps.focused}
        helperText={batchNumberError ?? (batchWarn ? aiHint : undefined)}
        // name senza la keyword "number": Chrome la userebbe per classificare
        // il campo come numero carta (vedi breakPaymentKeywords).
        name="lot-tracking-code"
        slotProps={{
          formHelperText: batchWarnProps.helperSx,
          input: {
            autoComplete: 'off',
            name: 'lot-tracking-code',
            inputMode: 'text',
            style: { fontSize: isMobile ? '16px' : undefined },
          },
        }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={isLooseProduce}
            onChange={(_, checked) => handleIsLooseProduceToggle(checked)}
            name="isLooseProduce"
          />
        }
        label={
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('looseProduceToggleLabel')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('looseProduceHelper')}
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', m: 0, '& .MuiFormControlLabel-label': { mt: 0.25 } }}
      />

      <ExistingLotWarning
        visible={showExistingLotWarning}
        canUseDdtReference={canUseDdtReference}
        onUseDdtReference={handleUseDdtReference}
      />

      <FormControlLabel
        control={
          <Switch
            checked={hasNoExpiry}
            onChange={(_, checked) => handleHasNoExpiryToggle(checked)}
            name="hasNoExpiry"
          />
        }
        label={
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('hasNoExpiryToggleLabel')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('hasNoExpiryHelper')}
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', m: 0, '& .MuiFormControlLabel-label': { mt: 0.25 } }}
      />

      <DatePicker
        label={breakPaymentKeywords(t('expiryDatePickerLabel'))}
        value={expiryDateValue}
        disablePast
        disabled={hasNoExpiry}
        onChange={handleExpiryChange}
        slotProps={{
          textField: {
            fullWidth: true,
            required: !hasNoExpiry,
            error: !!expiryDateError && !hasNoExpiry,
            color: expiryWarnProps.color,
            focused: expiryWarnProps.focused,
            helperText: hasNoExpiry
              ? undefined
              : (expiryDateError ?? (expiryWarn ? aiHint : undefined)),
            // name senza "scadenza": è la keyword "data scadenza carta" che,
            // insieme al numero lotto, fa scattare l'autofill dei pagamenti.
            name: 'use-by-date',
            slotProps: {
              formHelperText: expiryWarnProps.helperSx,
              htmlInput: {
                autoComplete: 'off',
                style: { fontSize: isMobile ? '16px' : undefined },
              },
            },
          },
        }}
      />

      {/* Quick-expiry row.
          Desktop: label + chip group + AI button on a single line.
          Mobile: label+button share the top row, chips drop underneath. */}
      <QuickExpiryRow
        isMobile={isMobile}
        activeValue={quickExpiryActiveValue}
        disabled={hasNoExpiry}
        onSelect={handleQuickExpiry}
        isSuggesting={suggestMutation.isPending}
        aiDisabled={aiButtonDisabled}
        aiTooltipKey={aiButtonTooltipKey}
        onAiSuggest={handleAiSuggest}
      />

      {showDateAiDisclaimer && (
        <Alert
          severity="info"
          icon={<GavelOutlined fontSize="small" />}
          sx={{
            alignItems: 'flex-start',
            py: 0.75,
            '& .MuiAlert-message': { fontSize: '0.78rem', lineHeight: 1.45 },
          }}
        >
          <Typography
            component="span"
            sx={{
              display: 'block',
              fontWeight: 700,
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              mb: 0.25,
            }}
          >
            {t('aiExpirySuggestionDisclaimerEyebrow')}
          </Typography>
          {t('aiExpirySuggestionDisclaimerBody')}
        </Alert>
      )}

      {showShelfStableSuggestion && (
        <Alert
          severity="info"
          variant="outlined"
          action={
            <Button size="small" color="inherit" onClick={() => handleHasNoExpiryToggle(true)}>
              {t('hasNoExpiryToggleActivate')}
            </Button>
          }
        >
          <AlertTitle sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
            {t('aiShelfStableTitle')}
          </AlertTitle>
          {aiSuggestion?.reasoning}
        </Alert>
      )}

      {showAiUnableToEstimate && (
        <Alert severity="warning" variant="outlined">
          {aiSuggestion?.reasoning}
        </Alert>
      )}
    </Box>
  );
}
